import type { TourCalloutHandles } from '@/hooks/useTourCallout';
import { trackAwayCalendarTutorial, type AwayCalendarTutorialStatus } from '@/util/analyticsEvents';

export const AWAY_CALENDAR_TOUR_KEY = 'away-calendar';

/** The tour has one stop, the Availability row, so it is the first and only zone. */
export const AWAY_CALENDAR_TOUR_ZONE = 1;

/** Two short lines in one card. Says what the row does, not why an expert would be away. */
export const AWAY_CALENDAR_TOUR_COPY = {
  steps: [
    'Pause new inquiries for the dates you pick.',
    // A non-breaking space, so "day." never sits alone on the last line.
    'They resume automatically after your last\u00A0day.',
  ],
  done: 'Got it',
} as const;

/** One paragraph, so each sentence does not start a line and leave the last word of the one before alone. */
export const AWAY_CALENDAR_TOUR_TEXT = AWAY_CALENDAR_TOUR_COPY.steps.join(' ');

type TourGate = {
  /** An expert who can see the calendar, with their account loaded. */
  isEligible: boolean;
  /** The callouts query has loaded and this expert has not resolved the tour. */
  shouldShowCallout: boolean;
  /** The Account screen is the one on top. */
  isFocused: boolean;
  /** A modal, sheet or form of the screen is open, which the tour must never sit over. */
  isCovered: boolean;
};

/** Whether the tour may start now. Every condition must hold, and any one lapsing stops it. */
export function canStartAwayCalendarTour({
  isEligible,
  shouldShowCallout,
  isFocused,
  isCovered,
}: TourGate): boolean {
  return isEligible && shouldShowCallout && isFocused && !isCovered;
}

/**
 * Records what happened to the tour, each at most once per mount: `shown` when
 * it reaches the screen, then one of `completed` or `dismissed`, which also
 * resolve the callout so it never comes back. Leaving the screen mid-tour
 * records neither, so the tour comes back the next time the expert opens Account.
 */
export function createAwayCalendarTourOutcomes(callout: { current: TourCalloutHandles }) {
  let hasShown = false;

  let hasResolved = false;

  return {
    shown: () => {
      if (hasShown) {
        return;
      }

      hasShown = true;

      callout.current.markShown();

      trackAwayCalendarTutorial({ status: 'shown' });
    },

    resolve: (status: Exclude<AwayCalendarTutorialStatus, 'shown'>) => {
      if (hasResolved) {
        return;
      }

      hasResolved = true;

      callout.current.markSeen();

      trackAwayCalendarTutorial({ status });
    },
  };
}
